import Vue, { DirectiveOptions } from 'vue';
import { VBPopover } from 'bootstrap-vue';
import { AxiosResponse } from 'axios';
import { Dictionary } from 'vue-router/types/router';

export function getValidationErrors(response: AxiosResponse<any>): Dictionary<string[]> {
    if (response && response.data && response.data.errors) {
        return response.data.errors as Dictionary<string[]>;
    }
    return {};
}

type placementOptions = 'auto' | 'top' | 'bottom' | 'left'
    | 'right' | 'topleft' | 'topright' | 'bottomleft'
    | 'bottomright' | 'lefttop' | 'leftbottom' | 'righttop' | 'rightbottom';
type fallbackPlacementOptions = 'flip' | 'clockwise' | 'counterclockwise' | placementOptions[];
interface DirectiveValues {
    unbindWatch: () => void;
    tooltip: DirectiveOptions;
    generateBindings: () => {
        name: string;
        value: {
            html: boolean;
            placement: placementOptions;
            title: () => string;
            trigger: string;
            offset?: number;
            fallbackPlacement?: fallbackPlacementOptions;
            customClass?: string;
            disabled: boolean;
        };
        modifiers: {};
    };
}

const dataElementTagNames = [
    'INPUT',
    'TEXTAREA',
    'SELECT',
    'DATALIST'
];

function findDataElement(element: HTMLElement): HTMLElement {
    if (dataElementTagNames.indexOf(element.tagName) !== -1) {
        return element;
    }
    const findByTag = (searchElement: HTMLElement, tag: string): HTMLElement | null => {
        const elements = searchElement.getElementsByTagName(tag);
        if (elements.length) {
            return elements[0] as HTMLElement;
        }
        return null;
    };

    return findByTag(element, 'input')
        || findByTag(element, 'textarea')
        || findByTag(element, 'select')
        || findByTag(element, 'datalist')
        || element;
}

const map = new Map<Element, DirectiveValues>();

export default Vue.directive('validation-error', {
    inserted(element, binding, node, oldNode) {
        let fieldName = '';
        let errorsProperty = 'validationErrors';
        if (typeof binding.value === 'string') {
            fieldName = binding.value;
        }
        else if (typeof binding.value === 'object') {
            fieldName = binding.value.fieldName || fieldName;
            errorsProperty = binding.value.errorsProperty || errorsProperty;
        }

        const dataElement = findDataElement(element);

        const errorMessageList = document.createElement('ul');
        errorMessageList.classList.add('error-msg-list');

        if (!node.context || !node.data || !node.data.directives) {
            return;
        }

        const directiveValues: DirectiveValues = {
            tooltip: Vue.directive('b-popover', VBPopover),
            unbindWatch: () => undefined,
            generateBindings: () => {
                return {
                    name: 'b-popover',
                    value: {
                        placement: 'top',
                        fallbackPlacement: 'clockwise',
                        // hide when no errors; couldn't get disabled prop working
                        customClass: errorMessageList.children.length ? 'error-msg-popup' : 'd-none',
                        html: true,
                        title: () => 'Validation Errors',
                        content: () => errorMessageList.outerHTML,
                        trigger: 'focus',
                        disabled: false
                    },
                    modifiers: {
                        ['v-danger']: true,
                    }
                };
            }
        };

        map.set(dataElement, directiveValues);

        const bindings = directiveValues.generateBindings();
        if (directiveValues.tooltip.bind) {
            directiveValues.tooltip.bind(dataElement, bindings, node, oldNode);
        }

        const model = node.data.directives.find(item => item.name === 'model')
            || (node.data as any).model;
        if (model && !fieldName) {
            const splitExpr = model.expression.split('.');
            fieldName = splitExpr[splitExpr.length - 1];
        }

        directiveValues.unbindWatch = node.context.$watch(errorsProperty, (errors: Dictionary<string[]>) => {
            errorMessageList.childNodes.forEach(child => errorMessageList.removeChild(child));
            dataElement.classList.remove('has-errors');

            let fieldErrors: string[] = [];
            for (const key in errors) {
                if (key.localeCompare(fieldName, undefined, { sensitivity: 'accent' }) === 0) {
                    fieldErrors = errors[key];
                    dataElement.classList.add('has-errors');
                    break;
                }
            }

            for (const message of fieldErrors) {
                const errorElement = document.createElement('li');
                errorElement.innerText = message;
                errorMessageList.appendChild(errorElement);
            }

            if (directiveValues.tooltip.componentUpdated) {
                const regenerated = directiveValues.generateBindings();
                directiveValues.tooltip.componentUpdated(dataElement, regenerated, node, oldNode);
            }
        });
    },
    componentUpdated(element, _, node, oldNode) {
        const directiveValues = map.get(element);
        if (directiveValues && directiveValues.tooltip.componentUpdated) {
            const bindings = directiveValues.generateBindings();
            directiveValues.tooltip.componentUpdated(element, bindings, node, oldNode);
        }
    },
    unbind(element, _, node, oldNode) {
        const directiveValues = map.get(element);
        if (directiveValues && directiveValues.unbindWatch) {
            directiveValues.unbindWatch();
        }
        if (directiveValues && directiveValues.tooltip.unbind) {
            directiveValues.tooltip.unbind(element, directiveValues.generateBindings(), node, oldNode);
        }

        map.delete(element);
    },
});
